/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'
import defaultLocale from '@/stores/defaultLocale'
import getLocale from '@/stores/locales'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import styled from 'styled-components'

import { useCartContext } from '@mercadoni/sinister/dist/cjs/cart'
import { Header as MainHeader } from '@mercadoni/sinister/dist/cjs/header'
import { useMessagesContext } from '@mercadoni/sinister/dist/cjs/shared/contexts'
import { useIsMobile } from '@mercadoni/sinister/dist/cjs/shared/hooks'
import { ClientModel } from '@mercadoni/sinister/dist/lib/src/entities/clientModel'

import { useAddressContext } from '@/context/AddressContext'
import { useAuthContext } from '@/context/AuthContext'
import { useOperationalModelContext } from '@/context/OperationalModelContext'
import { useStoreContext } from '@/context/StoreContext'

import useCartDrawer from '@/hooks/useCartDrawer'

import goToAccount from '@/navigation/CSR/goToAccount/goToAccount'
import goToCategory from '@/navigation/CSR/goToCategory'
import goToFavorites from '@/navigation/CSR/goToFavorites'
import goToLocation from '@/navigation/CSR/goToLocation'
import goToLogin from '@/navigation/CSR/goToLogin'
import goToProduct from '@/navigation/CSR/goToProduct'
import goToPromotions from '@/navigation/CSR/goToPromotions'
import goToSearch from '@/navigation/CSR/goToSearch/goToSearch'

import { goToHomeHybridMod } from '@/navigation/CSR/goToHomeHybridMod'
import { Header as HeaderSSR } from '@/sinister/header/export'
import getClientLocale from '@/utils/getClientLocale'
import { CategoryModel } from '@mercadoni/sinister/dist/lib/src/entities/category'
import { useEffect } from 'react'
import { CategoryPathProps } from '~types/index'
import setClientLocale from './setClientLocale'

const HeaderStyled = styled.div`
  position: sticky;
  top: 0px;
  z-index: var(--z-index-9);
  box-shadow: var(--shadow-boxShadow);
  .header__main > .ant-dropdown {
    .ant-btn {
      display: flex;
      justify-content: flex-start;
    }
  }
`

const Header = ({
  revalidatePath,
  clientModel,
  userAgent,
  categoryTree,
  showLocalHeader,
}: {
  revalidatePath: (url: string) => Promise<void>
  clientModel: ClientModel
  userAgent?: string
  categoryTree: CategoryModel[]
  showLocalHeader: boolean
}) => {
  const {
    header,
    disableGuestCart,
    clientId,
    theme,
    locale,
    availableLocales = [],
    messages,
    help,
    helpV3,
    showAddressLabel,
    slug,
    logo,
  } = clientModel

  const router = useRouter()
  const [isMobile] = useIsMobile(userAgent)

  const copies = getLocale() || defaultLocale
  const pathname = usePathname()
  const { customer } = useAuthContext()
  const { store } = useStoreContext()
  const { operationalModel } = useOperationalModelContext()
  const { address } = useAddressContext()

  const { cart } = useCartContext()
  const { address: cartAddress } = cart
  const cartOpModel = { opsModel: cart?.operationalModel }

  const {
    chooseOpModelMessage: { show: showChooseOpModelMessage },
    onChangeOpModelMessage,
  } = useMessagesContext()

  const { onOpenCart } = useCartDrawer()
  const { desktop } = theme?.header || {}
  const { isLightBackground } = desktop || {}

  const onClickAccountOption = async (action: string) => {
    goToAccount(router, action)
    if (action === 'log-out') {
      await goToHomeHybridMod(router)
      if (typeof window !== 'undefined') {
        window.location.reload()
      }
    }
  }

  const onClickOpModel = async () => {
    if (disableGuestCart && !customer) {
      goToLogin(router)
      return
    }
    goToLocation(router)
  }

  const onClickLogo = async () => await goToHomeHybridMod(router)

  const onCategoryOnClick = (category: CategoryPathProps) => {
    goToCategory(router, category)
  }

  const onPromotionsOnClick = () => {
    goToPromotions(router)
  }

  const onClickSearch = (queryString: string) => {
    if (!queryString?.length) return
    goToSearch(router, queryString, revalidatePath)
  }

  const onClickSuggested = (productSlug: string) => {
    goToProduct(router, productSlug)
  }

  const onClickFavorites = () => {
    goToFavorites(router)
  }

  const languagesConfig = {
    selected: getClientLocale({ locale }),
    onSelectLanguage: (code: string) => setClientLocale(code),
    enabled: availableLocales?.length >= 2,
    availableLocales,
  }

  const placeholder = header?.newPlaceholder
    ? copies?.showcase?.subheader?.placeholder || ''
    : copies?.showcase?.subheader?.searchin?.(store?.name || '') || ''

  useEffect(() => {
    if (!messages?.chooseOpModel) {
      return
    }
    onChangeOpModelMessage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, messages])

  const HeaderWrapper = showLocalHeader ? HeaderSSR : MainHeader

  return (
    <HeaderStyled id="header-container" className="header-container">
      <HeaderWrapper
        slug={slug || ''}
        domain={typeof window !== 'undefined' ? window.location.origin : ''}
        logo={logo || ''}
        layout={
          header?.layout || {
            desktop: 'A',
            mobile: 'A',
            simple: 'A',
          }
        }
        {...header}
        isLightBackground={isLightBackground}
        simpleViewOptions={false}
        store={store}
        copies={copies}
        languagesConfig={languagesConfig}
        LinkComponent={({ href, external, ...props }: any) => (
          <Link href={external ? href : `/${href}`} {...props} />
        )}
        onClickOpModel={onClickOpModel}
        onClickLogo={onClickLogo}
        onPromotionsOnClick={onPromotionsOnClick}
        onCategoryOnClick={onCategoryOnClick}
        onClickFavorites={onClickFavorites}
        onClickAccountOption={onClickAccountOption}
        onClickCart={onOpenCart}
        initialSearchValue={''}
        clientId={clientId}
        onClickSearch={onClickSearch}
        onClickSuggested={onClickSuggested}
        showChooseOpModelMessage={showChooseOpModelMessage}
        customer={customer}
        opsmodel={cartOpModel || operationalModel}
        address={cartAddress || address}
        help={help}
        helpV3={helpV3}
        placeholder={placeholder}
        isMobile={isMobile}
        showAddressLabel={showAddressLabel}
        initialCategoryTree={categoryTree}
      />
      {/* <Link href="/login" prefetch /> Prefetching route */}
      {/* <Link href="/register" prefetch /> Prefetching route */}
    </HeaderStyled>
  )
}

export default Header
